import React from "react";
import ModalDialog from "./ModalDialog";
import { createPortal } from "react-dom";

import defaultStyles from "./styles";

export const portalClassName = "ReactModalPortal";
export const bodyOpenClassName = "ReactModal__Body--open";

function getParentElement(parentSelector: any) {
  return parentSelector();
}

/**
 * @description IModalDialogProps UI Properties
 * @warning !!! react >= 16 만 지원( createPortal 함수가 react 16부터 지원)
 * @interface IModalPortalProps
 */
interface IModalPortalProps {
  /**
   * @description 다이얼로그 출력 유/무 플래그
   */
  isOpen: boolean;
  /**
   * @description custom style 속성
   */
  style?: any;
  /**
   * @description ModalDialog에 적용할 Class 속성
   */
  className?: string | object | undefined;
  /**
   * @description  ModalDialog가 open 상태일때, Portal 역할을 하는 DIV 태그에 적용할 Class 속성
   */
  portalClassName?: string;
  /**
   * @description ModalDialog가 open 상태일때, Body 태그에 적용할 Class 속성
   */
  bodyOpenClassName?: string;
  /**
   * @description ModalDialog가 open 상태일때, HTML태그에 적용할 Class 속성
   */
  htmlOpenClassName?: string;
  /**
   * @description ModalDialog가 open 상태일때, 배경 div에 적용할 Class 속성
   */
  backgroundClassName?: string | object;
  /**
   * @description '다이얼로그 닫기 요청'부터 '다이얼로그 닫기 실행' 까지 대기시간
   */
  closeTimeoutMS?: number;
  /**
   * @description Portal이 생성될 Parent Element Setector
   */
  parentSelector?: HTMLElement;
  /**
   * @description ModalDialog가 Open된 후, 호출되는 Callback
   */
  onAfterOpen?: (() => void) | null;
  /**
   * @description ModalDialog가 Close된 후, 호출되는 Callback
   */
  onAfterClose?: (() => void) | null;
  /**
   * @description ModalDialog가 닫혀야될때 호출되는 Callback.
   * 구현 의도는 Owner가 Callback 함수에서 ModalDialog에 닫힘 상태를 전달하고
   * 리-렌더링하여 다이얼로그가 그려지지 않도록 하는 것
   */
  onRequestClose?: ((event: Event) => void) | null;
}

/**
 * @description IModalDialogState UI States
 *
 * @interface IModalDialogState
 */
interface IModalPortalState {}

/**
 *
 *
 * @export
 * @description Modal 다이얼로그를 출력하는 컴포넌트 UI.
 * 동작 1. 입력받은 parentSelector(default document)에 다이얼로그가 출력될 Portal div Element를 삽입
 * 동작 2-1. 다이얼로그가 Open 상태라면, 다이얼로그 Element를 생성해 Portal div element에 삽입한다.
 * 동작 2-2. 다이얼로그가 Close 상태라면, Portal div element 하위에 생성되어있는 다이얼로그 Element를 삭제한다.
 * @example
 * ` `` js
 * ...
 * openModal = () => {
 *  this.setState({ modalIsOpen: true });
 * };
 * closeModal = () => {
 * this.setState({ modalIsOpen: false });
 * };
 * ...
 * render = () =>{
 *    ...
 *      <ModalPortal
 *        isOpen={this.state.modalIsOpen}
 *        onRequestClose={this.closeModal}
 *        style={customStyles}
 *        />
 *  ...
 * }
 * ` ``
 * @class ModalPotal
 * @extends {React.Component<IModalPortalProps, IModalPortalState>}
 */
export default class ModalPotal extends React.Component<
  IModalPortalProps,
  IModalPortalState
> {
  /**
   * @description 다이얼로그가 생성되는 Portal Div Element
   *
   * @private
   * @type {HTMLDivElement}
   * @memberof ModalPotal
   */
  private portalDstElement: HTMLDivElement | null = null;

  /**
   * @description Dialog element Reference
   *
   * @private
   * @type {ModalDialog}
   * @memberof ModalPotal
   */
  private dialogElementRef: ModalDialog | null = null;

  /// default properties
  public static defaultProps = {
    isOpen: false,
    portalClassName: "",
    bodyOpenClassName: "",
    htmlOpenClassName: "",
    role: "dialog",
    closeTimeoutMS: 0,
    onAfterOpen: null,
    onAfterClose: null,
    onRequestClose: null,
    parentSelector: () => document.body
  };

  /**
   * @description 컴포넌트 생성 완료시, Portal Div Element를 parentSelector 하위에 생성한다.
   * @method componentDidMount
   * @memberof ModalPotal
   */
  componentDidMount = () => {
    const { portalClassName } = this.props;

    if (
      this.portalDstElement !== null &&
      this.portalDstElement !== undefined &&
      portalClassName !== undefined
    ) {
      this.portalDstElement.className = portalClassName;
      const parent = getParentElement(this.props.parentSelector);
      parent.appendChild(this.portalDstElement);
    }
  };

  /**
   * @description 컴포넌트 Property 업데이트 시, parent Element가 달라진 경우,
   * Portal div element의 위치를 변경한다.
   * @method componentDidUpdate
   * @param {*} prevProps   이전 Properties
   * @param {*} _           이전 state
   * @param {*} snapshot    이전/현재 Parent element에 대한 Snapshot
   * @memberof ModalPotal
   */
  componentDidUpdate(
    prevProps: IModalPortalProps,
    _: IModalPortalState,
    snapshot: any
  ) {
    const { portalClassName } = this.props;

    if (
      prevProps.portalClassName !== portalClassName &&
      this.portalDstElement !== null &&
      this.portalDstElement.className !== null &&
      this.portalDstElement.className !== undefined &&
      portalClassName !== undefined
    ) {
      this.portalDstElement.className = portalClassName;
    }

    const { prevParent, nextParent } = snapshot;
    if (nextParent !== prevParent) {
      prevParent.removeChild(this.portalDstElement);
      nextParent.appendChild(this.portalDstElement);
    }
  }

  /**
   * @description 컴포넌트 제거시, 생성된 Dialog 및 Portal div element를 제거한다.
   * @method componentWillUnmount
   * @returns
   * @memberof ModalPotal
   */
  componentWillUnmount() {
    if (!this.portalDstElement || !this.dialogElementRef) return;

    const state = this.dialogElementRef.state;
    const now = Date.now();
    const closesAt =
      state.isOpen &&
      this.props.closeTimeoutMS &&
      (state.closesAt || now + this.props.closeTimeoutMS);

    if (closesAt) {
      if (!state.beforeClose) {
        this.dialogElementRef.closeWithTimeout();
      }

      setTimeout(this.removePortal, closesAt - now);
    } else {
      this.removePortal();
    }
  }

  /**
   * @description 이전/현재 property 의 parent값을 저장하여
   * 컴포넌트의 Property가 업데이트 되면 Snapshot 파라미터로 전달한다.
   * @method getSnapshotBeforeUpdate
   * @memberof ModalPotal
   */
  getSnapshotBeforeUpdate = (prevProps: IModalPortalProps) => {
    const { parentSelector: prevParentSelector } = prevProps;
    const { parentSelector: nextParentSelector } = this.props;

    const prevParent = getParentElement(prevParentSelector);
    const nextParent = getParentElement(nextParentSelector);

    return { prevParent, nextParent };
  };

  /**
   * @description Portal div Element를 제거한다.
   * @method removePortal
   * @memberof ModalPotal
   */
  removePortal = () => {
    const parent = getParentElement(this.props.parentSelector);
    parent.removeChild(this.portalDstElement);
  };

  /**
   * @description Dialog 에 대한 reference element를 획득한다.
   * @method setDialogRef
   * @memberof ModalPotal
   */
  setDialogRef = (ref: ModalDialog | null) => {
    this.dialogElementRef = ref;
  };

  /**
   * @description ModalDialog를 Portal에 생성한다.
   * @method render
   * @memberof ModalPotal
   */
  render = () => {
    if (!this.portalDstElement) {
      this.portalDstElement = document.createElement("div");
    }
    return createPortal(
      <ModalDialog
        ref={this.setDialogRef}
        //defaultStyles={ModalDialog.defaultStyles}
        defaultStyles={defaultStyles}
        {...this.props}
      />,
      this.portalDstElement
    );
  };
}
